<script>
import { mapState, mapMutations, mapGetters, mapActions } from "vuex";
import { filter, get, orderBy, groupBy } from "lodash";
import Tile from '@/components/tile.vue';
import { BASE_PLAN_SUB_TYPES, CAMPAIGN_CODES, planTypes, BILLING_CYCLE} from "@/common/constants";
import base from '@/views/config/addon-cards/templates/base.vue';
import logo from '@/assets/plans/zoom_scheduler.svg';
import { ZOOM_MEETING_SUB_TYPES, DEFAULT_SCHEDULER_PLAN_CAPACITY, additionalZoomSchedulerAsAddonCardCheck, isEduSchCmpPlusContext, isProContext, isBizContext } from '@/plans/ZOOM_MEETING/common/utils';
import { pageConfig } from '@/common/service/page-config';
import { getPlanTypeByCategory } from '@/common/service/parse';
import { planTypesForMeeting } from '@/views/config/addon-cards/index.vue';
import { plansof } from '@/utils/chain';
import { isAllowSchedulerFreeTrial } from './common/freetrial'
import { isSimilarPbxBundle, isSoftBundleBizPlus } from '@/views/config/soft-bundle/helper';
import softBundlePlansMixin from '@/views/config/soft-bundle/mixin/soft-bundle-plan';

export default {
  name: 'ZoomSchedulerAddOnCard',
  mixins: [base, softBundlePlansMixin],
  components: {
    Tile,
  },
  for: (arg) => {
    if (isSoftBundleBizPlus() || isSimilarPbxBundle()) { return true; }

    if (isEduSchCmpPlusContext || (window.newBillingConfig.enableCampusScheduler && window?.$config('plan') === ZOOM_MEETING_SUB_TYPES.ZONE_EDU_SCH_CMP)) {
      return false;
    }
    const GET_FREETRIAL_PLANS_INDEX = 4
    if (plansof(arg[GET_FREETRIAL_PLANS_INDEX] || []).freeTrial().type(planTypes.ZOOM_SCHEDULER).has()) {
      return false
    }
    const GET_SELECTED_PLANS_INDEX = 2; // src/views/config/core.vue - function call - filterAddonForKeys
    return additionalZoomSchedulerAsAddonCardCheck(arg[GET_SELECTED_PLANS_INDEX]) ? [...planTypesForMeeting(), ...planTypes.map([planTypes.ZOOM_DOCS])] : []
  },
  created() {
    const { amount } = this.lowestPlan;
    this.subtitle = this.$t('billing.common.form_xx_month', [this.$money(amount)]);
    this.productPlans = this.currentProductPlans;
    this.initTrialData()
  },
  data() {
    return {
      logo,
      title: this.$t('billing.scheduler.title'),
      subtitle: '',
      planType: planTypes.ZOOM_SCHEDULER,
      description: this.$t('billing.scheduler.desc'),
      form: DEFAULT_SCHEDULER_PLAN_CAPACITY,
      trialInited: false
    }
  },
  watch: {
    trialInfoMap() {
      this.updatePlanTrialStatus()
    },
    isAllowSchedulerFreeTrial() {
      this.updatePlanTrialStatus()
    },
    isSuccessPage() {
      this.updatePlanTrialStatus()
    },
    hasAndOnlyHasPro() {
      this.initTrialData()
    }
  },
  computed: {
    ...mapGetters(['getSelectedPlans', 'getCurrentBasePlanInfo', 'planMapFlat']),
    ...mapState({
      trialInfoMap: state => state.ShoppingCart.trialInfoMap
    }),
    currentProductPlans() {
      return orderBy(filter(this.planMapFlat, (p) => p.type === planTypes.ZOOM_SCHEDULER && !p.isOneMonth), ['participants', 'amount'], 'asc');
    },
    lowestPlan() {
      return get(this.currentProductPlans, [0], {});
    },
    basePlanBillingCycle () {
      const basePlanTypes = getPlanTypeByCategory(pageConfig.plan || 'pro')
      return this.getSelectedPlans.find((item) => item.type === basePlanTypes)?.billing_cycle || this.form.billingCycle;
    },
    zonePlans() {
      return this.planMapFlat.filter((item) => item.type === planTypes.ZOOM_SCHEDULER);
    },
    zonePlanGroupByCategory() {
      return groupBy(this.zonePlans, 'category');
    },
    // current selected meeting plan
    formPlan() {
      let formPlan;
      const enableBillingCycle = this.soft_bundle_enableBillingCycle;

      if (enableBillingCycle) {
        formPlan = this.zonePlanGroupByCategory?.[this.form.category]?.find?.(
          (item) => item.billing_cycle === enableBillingCycle,
        )
      }

      if (!formPlan) {
        formPlan = this.zonePlanGroupByCategory?.[this.form.category]?.find?.(
          (item) => item.billing_cycle === this.basePlanBillingCycle,
        )
      }

      if (!formPlan) {
        formPlan = this.zonePlanGroupByCategory?.[this.form.category]?.find?.(
          (item) => item.billing_cycle === BILLING_CYCLE.ANNUAL,
        );
      }
      return formPlan;
    },
    isAllowSchedulerFreeTrial() {
      return isAllowSchedulerFreeTrial()
    },
    hasAndOnlyHasPro() {
      const plansofZoomMeeting = plansof(this.getSelectedPlans).type(planTypes.ZOOM_MEETING)
      return plansofZoomMeeting.clone().has(1) && plansofZoomMeeting.clone().category(BASE_PLAN_SUB_TYPES.PRO).has()
    }
  },
  methods: {
    ...mapMutations(['updateSelectPlan', 'removePlanFreeTrial', 'setPlanFreeTrial']),
    ...mapActions(['fetchTrialInfos']),
    addOn(evt, addTrial) {
      // addPlanFromEnterKey For A11Y requirement
      // target is BUTTON comes from keyboard enter, will auto focaus on the first option in config card
      // target is SPAN comes from click
      const addPlanFromEnterKey = get(evt?.target, 'tagName', '') == 'BUTTON';
      if (addPlanFromEnterKey) {
        document.addPlanFromEnterKey = true;
      }
      if (!addTrial) {
        this.removePlanFreeTrial({ type: planTypes.ZOOM_SCHEDULER })
      }
      this.updateSelectPlan({
        ...this.formPlan,
        cartInfo: {
          selected_number: this.form.selectedNumber,
        },
      });
      this.$emit('add', this.$options?.key);
      this.$tracking.addOnBuyInteraction(`${this.trackingBasePlanName} add-ons`, `add-ons - ${this.title?.toLowerCase()} - ${this.trackingAddAction}`);
      this.$nextTick(this.focusToConfig);
    },
    addFreeTrial(evt) {
      this.setPlanFreeTrial({ type: planTypes.ZOOM_SCHEDULER })
      this.addOn(evt, true)
    },
    initTrialData() {
      if (this.trialInited) return
      const campainCode = CAMPAIGN_CODES.SCHEDULER
      if (
        (this.$context.is.buyNow || this.$context.is.basic2pro) &&
        (isProContext || isBizContext) &&
        this.hasAndOnlyHasPro &&
        pageConfig?.enableBaseWithFreeTrialCheckout &&
        !this.isSuccessPage &&
        this.$context.currency !== 'INR' &&
        (this.trialInfoMap[campainCode] || []).length < 2
      ) {
        try {
          this.fetchTrialInfos(campainCode)
        } catch {
          this.isFreeTrial = false
        } finally {
          this.trialInited = true
        }
      }
      this.updatePlanTrialStatus()
    },
    updatePlanTrialStatus() {
      if (!this.isAllowSchedulerFreeTrial || this.isSuccessPage) {
        this.isFreeTrial = false
        this.freeTrialDays = 0
        const { amount } = this.lowestPlan;
        this.subtitle = this.$t('billing.common.form_xx_month', [this.$money(amount)]);
        this.productPlans = this.currentProductPlans;
      } else {
        const basePlan = this.getCurrentBasePlanInfo?.plans[0]
        if (!basePlan) {
          return
        }
        const trialPlan = []
        const trialInfoList = this.trialInfoMap[CAMPAIGN_CODES.SCHEDULER] || []
        if (trialInfoList.length < 2) {
          return
        }
        for (const info of trialInfoList) {
          const plan = plansof(this.planMapFlat).type(planTypes.ZOOM_SCHEDULER).code(info.planCode).get(0)
          if (plan.billing_cycle === basePlan.billing_cycle) {
            this.isFreeTrial = true
            this.freeTrialDays = info.trialDays
          }
          trialPlan.push(plan)
        }
        const lowestPlan = get(orderBy(trialPlan, ['participants', 'amount'], 'asc'), [0])
        if (lowestPlan) {
          const lowestInfo = trialInfoList.find((item) => item.planCode === lowestPlan.plan_code)
          this.subtitle = this.$t('billing.common.from_xx_month_after_trial', {
            price: this.$money(lowestPlan.amount),
            duration: lowestInfo.trialDays
          });
        }
      }
    }
  }
};
</script>
<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';
html:lang(fr-FR), html:lang(es-ES), html:lang(de-DE), html:lang(pt-PT), html:lang(it-IT), html:lang(nl-NL) {
  .opc-addon__free-trial {
    @include media(mobile) {
      .opc-addon__head {
        margin-right: 0 !important;
      }
      .opc-addon__action {
        position: static;
        margin-top: 14px;
        &>button {
          width: 100%;
        }
        &-sub {
          text-align: center;
          margin-top: 8px;
          margin-right: 4px;
        }
      }
    }
  }
}
</style>
